import { ApiError } from '@lib/api';

import { SLUG_PATTERN, USERNAME_MAX, USERNAME_PATTERN } from '../schemas/client';

/**
 * The wizard's rules, kept out of the component so they can be read — and
 * eventually tested — without rendering anything.
 */

export interface Draft {
    name: string;
    slug: string;
    slugTouched: boolean;
    orgId: string;
    isSandbox: boolean;
    username: string;
    email: string;
}

export const EMPTY_DRAFT: Draft = {
    name: '',
    slug: '',
    slugTouched: false,
    orgId: '',
    isSandbox: false,
    username: '',
    email: '',
};

/**
 * The address, derived from the name until the operator edits it.
 *
 * Deriving is a courtesy, not a rule: once they type in the field it is theirs,
 * because this string is permanent and public and they may well want something
 * other than a transliteration of the legal name.
 */
export function slugify(name: string): string {
    return name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64);
}

export function slugError(slug: string): string | undefined {
    if (slug.length === 0) return undefined;
    return SLUG_PATTERN.test(slug)
        ? undefined
        : 'Lowercase letters, numbers and hyphens only. No spaces or accents.';
}

export function usernameError(username: string): string | undefined {
    if (username.length === 0) return undefined;
    if (username.length > USERNAME_MAX) {
        return `At most ${String(USERNAME_MAX)} characters — that is Cognito’s limit, not ours.`;
    }
    return USERNAME_PATTERN.test(username) ? undefined : 'No spaces or control characters.';
}

export function emailError(email: string): string | undefined {
    if (email.length === 0) return undefined;
    return /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email)
        ? undefined
        : 'That email is not a valid address.';
}

export const isStepOneComplete = (draft: Draft): boolean =>
    draft.name.trim().length > 0 && SLUG_PATTERN.test(draft.slug);

export const isStepTwoComplete = (draft: Draft): boolean =>
    usernameError(draft.username) === undefined &&
    draft.username.length > 0 &&
    emailError(draft.email) === undefined &&
    draft.email.length > 0;

/**
 * What a failed creation means for the screen.
 *
 * The three outcomes are not three messages — they are three different
 * *situations*, and they differ in what already exists:
 *
 * - `address` — nothing was created. Change the address and submit again.
 * - `owner` — the client exists and its person does not. Change a field on the
 *   owner step; the whole request cannot be retried, because it would now fail
 *   on the address this half-created client holds.
 * - `pending` — the client exists, the person does not, and nothing the
 *   operator typed is wrong. This is the only one where retrying is right.
 *
 * Everything else is `unknown` and gets the message as-is. That is deliberate:
 * a code we do not recognise should degrade to "something went wrong" rather
 * than be guessed into one of the branches above.
 */
export type CreateFailure =
    | { kind: 'address'; message: string }
    | { kind: 'owner'; message: string; field: 'username' | 'email' | 'unknown'; tenantId?: string }
    | { kind: 'pending'; message: string; tenantId?: string }
    | { kind: 'unknown'; message: string };

export function classifyFailure(error: Error): CreateFailure {
    if (!(error instanceof ApiError)) {
        return { kind: 'unknown', message: error.message };
    }

    switch (error.code) {
        case 'ADDRESS_TAKEN':
            return { kind: 'address', message: error.message };
        case 'OWNER_CONFLICT':
            return {
                kind: 'owner',
                message: error.message,
                // May be `unknown`: upstream answers 409 for both the username
                // and the email, and the two differ only in prose. Marking the
                // wrong input is cheap; claiming certainty is not.
                field: readConflictField(error.detail('field')),
                ...(error.detail('tenantId') !== undefined
                    ? { tenantId: error.detail('tenantId') }
                    : {}),
            };
        case 'OWNER_NOT_PROVISIONED':
            return {
                kind: 'pending',
                message: error.message,
                ...(error.detail('tenantId') !== undefined
                    ? { tenantId: error.detail('tenantId') }
                    : {}),
            };
        default:
            return { kind: 'unknown', message: error.message };
    }
}

function readConflictField(value: string | undefined): 'username' | 'email' | 'unknown' {
    return value === 'username' || value === 'email' ? value : 'unknown';
}

/** Which step a failure sends the operator back to. */
export function stepForFailure(failure: CreateFailure): 1 | 2 | 3 {
    if (failure.kind === 'address') return 1;
    if (failure.kind === 'owner') return 2;
    return 3;
}
